import { useSpring } from '@react-spring/core'
import { animated } from '@react-spring/web'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

type Props = {
  totalSteps: number
  activeSlide: number
  moveToNextSlide: () => void
  isAnswerValidated: boolean
  showProblemNumber: boolean
  isPaused: boolean
}

const AutoSlideSwitcher = ({
  totalSteps,
  activeSlide,
  moveToNextSlide,
  isAnswerValidated,
  showProblemNumber,
  isPaused,
}: Props) => {
  const [isAutoSwitching, setIsAutoSwitching] = useState(true)
  // Used to determine whether to stop auto switching or not
  const [autoSwitchedActiveSlide, setAutoSwitchedActiveSlide] = useState(
    activeSlide
  )

  // Creating an array of length totalSteps. Will be used to display bottom progress bars.
  const bars = useMemo(
    () => Array.from({ length: totalSteps }, (_, index) => index),
    [totalSteps]
  )

  const handleMoveToNextSlide = () => {
    setAutoSwitchedActiveSlide((prev) => prev + 1)
    moveToNextSlide()
  }

  const [barStyles, barAnimationAPI] = useSpring(() => ({
    from: { width: '0%' },
    config: { duration: 5000 },
  }))

  // If the user is not navigating through problems, activeSlide and autoSwitchedActiveSlide will be the same.
  // If the values are not, that means we must stop auto switching.
  useEffect(() => {
    if (
      activeSlide !== autoSwitchedActiveSlide ||
      (isAnswerValidated && isPaused)
    )
      setIsAutoSwitching(false)
  }, [activeSlide, isPaused, isAnswerValidated])

  // Reseting auto switch when a new question loads
  useEffect(() => {
    if (activeSlide === 0 && !isAnswerValidated) {
      setIsAutoSwitching(true)
      setAutoSwitchedActiveSlide(0)
      barAnimationAPI.update({ from: { width: '0%' } })
    }
  }, [activeSlide, isAnswerValidated])

  useEffect(() => {
    let interval = null
    if (isAnswerValidated && isAutoSwitching) {
      interval = setInterval(() => {
        handleMoveToNextSlide()
        barAnimationAPI.set({ width: '0%' })
      }, 5000)
      barAnimationAPI.update({ to: { width: '100%' } }).start()
    } else {
      clearInterval(interval)
      barAnimationAPI.set({ width: '0%' })
    }
    return () => clearInterval(interval)
  }, [isAnswerValidated, isAutoSwitching, handleMoveToNextSlide])

  return (
    <div
      className={classNames(
        'absolute bottom-0 mb-12 pb-1 right-0 left-0 mx-auto w-1/4 flex space-x-1 justify-center',
        {
          'opacity-0':
            showProblemNumber || !isAnswerValidated || activeSlide < 0,
        }
      )}
    >
      {bars.map((barNumber) => (
        <div
          key={barNumber}
          className={classNames('relative w-6 lg:w-10 h-2 rounded-md', {
            'bg-white': barNumber < activeSlide,
            'bg-grey-700': barNumber >= activeSlide,
          })}
        >
          <animated.div
            style={
              isAutoSwitching && barNumber === activeSlide
                ? barStyles
                : barNumber <= activeSlide
                ? { width: '100%' }
                : { width: '0%' }
            }
            className="bg-white duration-75 h-full rounded-md"
          />
        </div>
      ))}
    </div>
  )
}

export default AutoSlideSwitcher
